import type { FC, ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Column, ContentSwitcher, Grid, InlineLoading, Layer, Link, RadioTile, Stack, Switch, TextInput, Tile, TileGroup } from '@carbon/react';
import { ArrowRight, Purchase } from '@carbon/icons-react';

import { formatCardNumber, formatCep, formatCpfCnpj, formatExpiry, formatPhone } from '@maestra/core/utils/asaasForm';
import type { DadosDoCheckout, MetodoDePagamento } from '../../../logica/dinheiro/useCheckoutDaAssinatura';
import { LogoPix } from '../../marcas/LogosDeTerceiros';
import { ROTULOS } from '../../padroes/rotulos';
import { abrirCodigoDeAcesso } from '../../../logica/dinheiro/codigoDeAcesso';
import { Adicionais, CampoDoCupom, ErroDaCobranca, Recebedor, Resumo } from './Pecas';
import styles from './Dinheiro.module.scss';

// O CHECKOUT DA ASSINATURA, no Carbon: à esquerda, o que se preenche (o CPF, a forma de pagamento
// e o cartão); à direita, o que se paga (o ciclo, os adicionais, o cupom, o resumo e o botão). O
// mesmo checkout mora na /planos e no desbloqueio de um perfil.
//
// O botão fica sempre ativo: clicado com campo faltando, os erros aparecem em cada campo e o foco
// vai para o primeiro.

const focarNoPrimeiroErro = () => requestAnimationFrame(() => {
  const campo = document.querySelector<HTMLElement>('[data-checkout] input[data-invalid="true"]');
  campo?.focus();
  campo?.scrollIntoView?.({ block: 'center' });
});

export const Checkout: FC<{ c: DadosDoCheckout; titulo?: string; rodape?: ReactNode }> = ({ c, titulo, rodape }) => {
  const f = c.form;
  const e = f.fieldErrors;
  const campo = (id: string, rotulo: string, valor: string, mudar: (v: string) => void, erro: string | undefined, extra: Record<string, unknown> = {}) => (
    <TextInput id={id} labelText={rotulo} value={valor} onChange={(ev) => mudar(ev.target.value)} invalid={!!erro} invalidText={erro} {...extra} />
  );
  // No desbloqueio de um perfil, o código vai para ele; na /planos, o modal pergunta o perfil.
  const { id: artistId } = useParams();
  const pagar = async () => { const ok = await c.pagar(); if (!ok) focarNoPrimeiroErro(); };

  return (
    <Grid narrow className={styles.checkout} data-checkout=''>
      <Column sm={4} md={8} lg={9}>
        <Stack gap={6}>
          <p className={styles.apoio}>{`Assinando como ${c.email}`}</p>
          <Tile className={styles.bloco}>
            <Layer>
              {/* Sem <form>: o Enter num campo não pode cobrar (no PIX, com um campo só, ele enviava). */}
              <div>
                <Stack gap={6}>
                  <h2 className={styles.tituloDoBloco}>{titulo ?? 'Insira as informações de pagamento para começar'}</h2>
                  {campo('checkout-cpf', 'CPF ou CNPJ', f.cpf, (v) => f.setCpf(formatCpfCnpj(v)), e.cpfCnpj, { placeholder: '000.000.000-00', inputMode: 'numeric', autoComplete: 'off' })}
                  <TileGroup name='forma-de-pagamento' legend='Forma de pagamento' valueSelected={c.metodo.valor}
                    onChange={(v) => c.metodo.mudar(v as MetodoDePagamento)} className={styles.formas}>
                    {/* A marca de cada forma ao lado do nome: o símbolo do PIX e um cartão. */}
                    <RadioTile id='forma-pix' value='PIX'>
                      <span className={styles.forma}>
                        <LogoPix size={24} className={styles.marcaDaForma} />
                        <span>
                          <span className={styles.nomeDoItem}>PIX</span>
                          <span className={styles.apoio}>Geramos um código; a assinatura ativa assim que o pagamento cai.</span>
                        </span>
                      </span>
                    </RadioTile>
                    <RadioTile id='forma-cartao' value='CREDIT'>
                      <span className={styles.forma}>
                        <Purchase size={24} className={`${styles.marcaDaForma} ${styles.iconeDaForma}`} aria-hidden />
                        <span>
                          <span className={styles.nomeDoItem}>Cartão de crédito</span>
                          <span className={styles.apoio}>A cobrança renova sozinha a cada ciclo.</span>
                        </span>
                      </span>
                    </RadioTile>
                  </TileGroup>
                  {c.metodo.valor === 'CREDIT' && (
                    <Stack gap={6}>
                      {campo('checkout-numero', 'Número do cartão', f.cardNumber, (v) => f.setCardNumber(formatCardNumber(v)), e.cardNumber, { placeholder: '0000 0000 0000 0000', inputMode: 'numeric', autoComplete: 'cc-number' })}
                      {campo('checkout-nome', 'Nome impresso no cartão', f.cardName, (v) => f.setCardName(v.toUpperCase()), e.cardName, { placeholder: 'Como aparece no cartão', autoComplete: 'cc-name' })}
                      <div className={styles.dupla}>
                        {campo('checkout-validade', 'Validade', f.cardExpiry, (v) => f.setCardExpiry(formatExpiry(v)), e.cardExpiry, { placeholder: 'MM/AA', inputMode: 'numeric', autoComplete: 'cc-exp' })}
                        {campo('checkout-cvv', 'CVV', f.cardCvv, (v) => f.setCardCvv(v.replace(/\D/g, '').slice(0, 4)), e.cardCvv, { placeholder: '123', inputMode: 'numeric', autoComplete: 'cc-csc' })}
                      </div>
                      {campo('checkout-celular', 'Celular', f.phone, (v) => f.setPhone(formatPhone(v)), e.phone, { placeholder: '(11) 99999-9999', inputMode: 'tel', autoComplete: 'tel' })}
                      <div className={styles.dupla}>
                        <div>
                          {campo('checkout-cep', 'CEP', f.cep, (v) => f.setCep(formatCep(v)), e.cep || f.cepLookupError || undefined, {
                            placeholder: '00000-000', inputMode: 'numeric', autoComplete: 'postal-code',
                            helperText: !f.cepLookupError && f.resolvedAddress?.localidade
                              ? [f.resolvedAddress.bairro, `${f.resolvedAddress.localidade} / ${f.resolvedAddress.uf}`].filter(Boolean).join(', ')
                              : undefined,
                          })}
                          {f.cepLoading && <InlineLoading description='Buscando o endereço…' iconDescription={ROTULOS.carregando} />}
                        </div>
                        {/* O número do endereço do titular (B2): ia sempre "0". */}
                        {campo('checkout-numero-endereco', 'Número', f.addressNumber, f.setAddressNumber, e.addressNumber, { placeholder: '123 ou S/N' })}
                      </div>
                    </Stack>
                  )}
                </Stack>
              </div>
            </Layer>
          </Tile>
          {rodape}
        </Stack>
      </Column>

      <Column sm={4} md={8} lg={7}>
        <Resumo
          titulo='Resumo da assinatura'
          item={c.item}
          linhas={c.linhas}
          total={c.total}
          depois={{ rotulo: c.renovacao.rotulo, valor: c.renovacao.valor, apoio: c.renovacao.apoio }}
          acima={(
            <Stack gap={6}>
              {c.ciclo && (
                <ContentSwitcher size='md' selectedIndex={c.ciclo.valor === 'YEARLY' ? 1 : 0}
                  onChange={({ index }) => c.ciclo?.mudar(index === 1 ? 'YEARLY' : 'MONTHLY')}>
                  <Switch name='MONTHLY' text='Mensal' />
                  <Switch name='YEARLY' text={c.ciclo.rotuloDoAnual} />
                </ContentSwitcher>
              )}
              <Adicionais linhas={c.adicionais} />
              <CampoDoCupom c={c.cupom} />
              {/* O código de acesso (Pass Access) libera o planejamento sem cobrança: a porta fica aqui
                  também, para quem chega ao checkout com um código na mão (13/10/2026). */}
              <div>
                <Button kind='ghost' size='md' onClick={() => abrirCodigoDeAcesso(artistId)}>Tenho um código de acesso</Button>
              </div>
            </Stack>
          )}
        >
          <p className={styles.apoio}>
            {`${c.legal} Você concorda com os `}
            <Link href='/legal/termos' target='_blank' rel='noopener noreferrer' inline>Termos de uso</Link>
            {' e a '}
            <Link href='/legal/privacidade' target='_blank' rel='noopener noreferrer' inline>Política de privacidade</Link>.
          </p>
          <ErroDaCobranca texto={c.erro} />
          {c.enviando
            ? <InlineLoading description='Processando o pagamento…' iconDescription={ROTULOS.carregando} />
            : <Button kind='primary' size='lg' renderIcon={ArrowRight} onClick={() => { void pagar(); }} className={styles.botaoCheio}>{c.botao}</Button>}
          {/* O que ainda falta, dito antes do clique (o antigo explicava no botão desabilitado). */}
          {!c.enviando && c.falta && <p className={styles.apoio}>{`Para pagar: ${c.falta}`}</p>}
          <Recebedor />
        </Resumo>
      </Column>
    </Grid>
  );
};
